'use client';
import { useSearchParams } from 'next/navigation';
import { memo, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getLocalizedUrl } from '~features/language/model/utils';
import { loadFromSessionStorage, ssKeys } from '~features/storage';
import { useSession } from '~hooks/useSession';
import { locales } from '~i18n/constants';
import { authApi } from '~services/api/auth';
import { TokenManager } from '~shared/services/ClientTokenManager';
import {
	getNSFWOrigin,
	getSFWOrigin,
	isDev,
	isSafeApp,
} from '~shared/utils/host';
import type { AppDispatch } from '~store/index';

import { StyledIFrame } from './styles';

type Params = { token?: string };

const fromOrigin = isSafeApp ? getNSFWOrigin() : getSFWOrigin();
const localizedOrigins = isDev()
	? [fromOrigin]
	: locales.map((loc) =>
			getLocalizedUrl(new URLSearchParams(), '', loc, fromOrigin)
	  );

export const AuthSyncIframe = memo(() => {
	const { isSuccess } = useSession();
	const searchParams = useSearchParams();
	const isInitialized = useRef(false);
	const dispatch = useDispatch<AppDispatch>();
	const [finished, setFinished] = useState(false);

	useEffect(() => {
		if (!isSuccess || isInitialized.current) {
			return;
		}
		isInitialized.current = true;

		const { token, type } = TokenManager.getInfo();
		const hasSavedRealToken = !!token && type !== 'demo';
		const isLoggedOutManual = loadFromSessionStorage(
			ssKeys.LOGGED_OUT_MANUAL,
			false
		);
		const shouldSkipLogin = hasSavedRealToken || isLoggedOutManual;

		if (shouldSkipLogin) {
			setFinished(true);
			return;
		}

		const listener = (event: MessageEvent) => {
			void (async () => {
				if (!localizedOrigins.includes(event.origin)) return;

				const { token } = (event.data || {}) as Params;

				if (!token) return;

				TokenManager.saveToken(token);
				await dispatch(
					authApi.endpoints.authenticate.initiate(undefined, {
						forceRefetch: true,
					})
				);

				window.removeEventListener('message', listener);
				setFinished(true);
			})();
		};

		window.addEventListener('message', listener);
		return () => window.removeEventListener('message', listener);
	}, [dispatch, isSuccess, searchParams]);

	if (!isSuccess || finished) {
		return null;
	}

	// iFrame for auth synchronization between SAFE and UNSAFE domains (joi.ai and joi.com for example)
	return <StyledIFrame src={`${fromOrigin}/auth/sync`} />;
});

AuthSyncIframe.displayName = 'AuthSyncIframe';
